import React from "react";
import Markdown from "markdown-to-jsx";

const elements = [
  "a",
  "abbr",
  "address",
  "area",
  "article",
  "aside",
  "audio",
  "b",
  "base",
  "bdi",
  "bdo",
  "big",
  "blockquote",
  "body",
  "br",
  "button",
  "canvas",
  "caption",
  "cite",
  "code",
  "col",
  "colgroup",
  "data",
  "datalist",
  "dd",
  "del",
  "details",
  "dfn",
  "dialog",
  "div",
  "dl",
  "dt",
  "em",
  "embed",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "head",
  "header",
  "hgroup",
  "hr",
  "html",
  "i",
  "iframe",
  "img",
  "input",
  "ins",
  "kbd",
  "keygen",
  "label",
  "legend",
  "li",
  "link",
  "main",
  "map",
  "mark",
  "marquee",
  "menu",
  "menuitem",
  "meta",
  "meter",
  "nav",
  "noscript",
  "object",
  "ol",
  "optgroup",
  "option",
  "output",
  "p",
  "param",
  "picture",
  "pre",
  "progress",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "script",
  "section",
  "select",
  "small",
  "source",
  "span",
  "strong",
  "style",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "textarea",
  "tfoot",
  "th",
  "thead",
  "time",
  "title",
  "tr",
  "track",
  "u",
  "ul",
  "var",
  "video",
  "wbr",

  // SVG
  "circle",
  "clipPath",
  "defs",
  "ellipse",
  "foreignObject",
  "g",
  "image",
  "line",
  "linearGradient",
  "marker",
  "mask",
  "path",
  "pattern",
  "polygon",
  "polyline",
  "radialGradient",
  "rect",
  "stop",
  "svg",
  "text",
  "tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

// Mesmo critério do autolink do markdown-to-jsx: pontuação final não faz parte da URL.
const URL_REGEX = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
const URL_TOKEN = (index) => `\u0000URL${index}\u0000`;
// \u0000 é o delimitador de URL_TOKEN: não aparece em texto digitado.
// eslint-disable-next-line no-control-regex
const URL_TOKEN_SPLIT_REGEX = /\u0000URL(\d+)\u0000/;
const MAX_LINK_LABEL = 60;

export const shortenUrlLabel = (url) => {
  if (typeof url !== "string" || url.length <= MAX_LINK_LABEL) return url;
  try {
    const { hostname, pathname } = new URL(url);
    const label = `${hostname.replace(/^www\./, "")}${pathname === "/" ? "" : pathname}`;
    return label.length > MAX_LINK_LABEL
      ? `${label.slice(0, MAX_LINK_LABEL - 1)}…`
      : `${label}…`;
  } catch {
    return `${url.slice(0, MAX_LINK_LABEL - 1)}…`;
  }
};

const CustomLink = ({ children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const convertWhatsAppFormatting = (text) => {
  const whatsAppBoldRegex = /\*(.*?)\*/g;
  const whatsAppItalicRegex = /_(.*?)_/g;
  const whatsAppStrikeRegex = /~(.*?)~/g;
  const pixMarker = '###PIX_MARKER###';

  let formattedText = text;

  if (formattedText.includes('***')) {
    formattedText = formattedText.replace(/\*\*\*/g, pixMarker);
  }

  if (whatsAppBoldRegex.test(formattedText)) {
    formattedText = formattedText.replace(whatsAppBoldRegex, "**$1**");
  }

  if (whatsAppItalicRegex.test(formattedText)) {
    formattedText = formattedText.replace(whatsAppItalicRegex, "*$1*");
  }

  if (whatsAppStrikeRegex.test(formattedText)) {
    formattedText = formattedText.replace(whatsAppStrikeRegex, "~~$1~~");
  }

  if (formattedText.includes(pixMarker)) {
    formattedText = formattedText.replace(new RegExp(pixMarker, 'g'), '***');
  }

  return formattedText;
};

// `_` e `*` são comuns em query string: a URL sai da formatação e vira <a> direto,
// senão o href clicável fica adulterado. O autolink do markdown-to-jsx também não
// serve: só reconhece URL no início de um trecho de texto.
export const splitTextAndUrls = (text) => {
  const urls = [];
  const tokenized = text.replace(URL_REGEX, (url) => {
    urls.push(url);
    return URL_TOKEN(urls.length - 1);
  });

  return convertWhatsAppFormatting(tokenized)
    .split(URL_TOKEN_SPLIT_REGEX)
    .map((part, index) =>
      index % 2 === 1 ? { url: urls[Number(part)] } : { text: part }
    )
    .filter((part) => part.url || part.text);
};

const MarkdownWrapper = ({ children }) => {
  const options = React.useMemo(() => {
    const markdownOptions = {
      disableParsingRawHTML: true,
      forceInline: true,
      overrides: {
        a: { component: CustomLink },
      },
    };

    elements.forEach(element => {
      if (!allowedElements.includes(element)) {
        markdownOptions.overrides[element] = el => el.children || null;
      }
    });

    return markdownOptions;
  }, []);

  const text = typeof children === "number" ? String(children) : children;
  if (!text || typeof text !== "string") return null;

  const parts = splitTextAndUrls(text);

  if (parts.length === 1 && parts[0].text !== undefined) {
    return <Markdown options={options}>{parts[0].text}</Markdown>;
  }

  // O markdown-to-jsx descarta espaço/quebra de linha no fim do trecho; sem isso
  // "*Nome:*\n<link>" grudaria a assinatura no link.
  return (
    <span>
      {parts.map((part, index) => {
        if (part.url) {
          return (
            <CustomLink key={index} href={part.url} title={part.url}>
              {shortenUrlLabel(part.url)}
            </CustomLink>
          );
        }
        const [, leading, core, trailing] = part.text.match(/^(\s*)([\s\S]*?)(\s*)$/);
        return (
          <React.Fragment key={index}>
            {leading}
            {core && <Markdown options={options}>{core}</Markdown>}
            {trailing}
          </React.Fragment>
        );
      })}
    </span>
  );
};

export default MarkdownWrapper;

